import {
  ElementRef,
  HostListener,
  Directive,
  Input,
  NgZone,
  OnDestroy,
  OnChanges,
  AfterContentChecked,
  inject,
} from '@angular/core';

import { fromEvent, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

const MAX_LOOKUP_RETRIES = 3;

@Directive({
  selector: '[autosize]',
  standalone: true,
})
export class AutoResizeDirective implements OnDestroy, OnChanges, AfterContentChecked {
  @Input() minRows: number;
  @Input() maxRows: number;
  @Input() onlyGrow = false;
  @Input() useImportant = false;

  private zone = inject(NgZone);
  private retries = 0;
  private textAreaEl: any;
  private oldContent: string;
  private oldWidth: number;
  private destroyed$ = new ReplaySubject(1);

  element = inject(ElementRef);

  @HostListener('input', ['$event.target'])
  onInput(textArea: HTMLTextAreaElement): void {
    this.adjust();
  }

  constructor() {
    if (this.element.nativeElement.tagName !== 'TEXTAREA') {
      this._findNestedTextArea();
    } else {
      this.textAreaEl = this.element.nativeElement;
      this.textAreaEl.style.overflow = 'hidden';
      this._onTextAreaFound();
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterContentChecked() {
    this.adjust();
  }

  ngOnChanges(changes) {
    this.adjust(true);
  }

  _findNestedTextArea() {
    this.textAreaEl = this.element.nativeElement.querySelector('TEXTAREA');

    if (!this.textAreaEl && this.element.nativeElement.shadowRoot) {
      this.textAreaEl = this.element.nativeElement.shadowRoot.querySelector('TEXTAREA');
    }

    if (!this.textAreaEl) {
      if (this.retries >= MAX_LOOKUP_RETRIES) {
        console.warn('ngx-autosize: textarea not found');
      } else {
        this.retries++;
        setTimeout(() => {
          this._findNestedTextArea();
        }, 100);
      }
      return;
    }

    this.textAreaEl.style.overflow = 'hidden';
    this._onTextAreaFound();
  }

  _onTextAreaFound() {
    this.zone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(takeUntil(this.destroyed$), debounceTime(200), distinctUntilChanged())
        .subscribe(() => {
          this.zone.run(() => {
            this.adjust();
          });
        });
    });
    setTimeout(() => {
      this.adjust();
    });
  }

  adjust(inputsChanged = false): void {
    if (this.textAreaEl) {
      const currentText = this.textAreaEl.value;

      if (inputsChanged === false && currentText === this.oldContent && this.textAreaEl.offsetWidth === this.oldWidth) {
        return;
      }

      this.oldContent = currentText;
      this.oldWidth = this.textAreaEl.offsetWidth;

      this.textAreaEl.style.height = 'auto';
      this.textAreaEl.style.height = `${this.textAreaEl.scrollHeight}px`;
    }
  }
}
